import { FunctionComponent, useState } from 'react';
import { Box, Drawer, SvgIcon, Typography } from '@mui/material';
import { StarjackTable } from '@/components/Table/v2/Table';
import { Mail } from '@/models/mail';
import { formatStarjackDate } from '@/utils/time';
import IconButton from '@mui/material/IconButton';
import { EnvelopeOpenIcon } from '@heroicons/react/24/solid';
import { MailDrawerContent } from '@/pageContents/mails/MailDrawerContent';
import { usePagination } from '@starjack/shared-components';
import { useGetMailsQuery } from '@/api/v2/mails';

type CustomerDetailsMailsTableProps = {
    customerId: string;
};

export const CustomerDetailsMailsTable: FunctionComponent<
    CustomerDetailsMailsTableProps
> = ({ customerId }) => {
    const {
        data: mails,
        isLoading,
        error,
    } = useGetMailsQuery({
        userId: customerId,
    });
    const {
        currentItems: currentMails,
        itemsPerPage: mailsPerPage,
        setNextPage: setMailsPage,
        page,
        countTotal,
        setItemsPerPage,
    } = usePagination({
        items: mails,
        itemsPerPage: 10,
    });

    const [mailDetailConfig, setMailDetailConfig] = useState<{
        isOpen: boolean;
        mail: Mail;
    }>({
        isOpen: false,
        mail: null,
    });

    const onCloseDrawer = () => {
        setMailDetailConfig((prev) => ({ ...prev, isOpen: false }));
    };

    return (
        <>
            <Box>
                <Typography
                    sx={{
                        mb: 4,
                    }}
                    variant='h5'
                >
                    Mails
                </Typography>

                <StarjackTable<Mail>
                    loading={isLoading}
                    error={error}
                    allItems={mails || []}
                    count={countTotal}
                    currentPage={page}
                    items={currentMails}
                    onPageChange={setMailsPage}
                    rowsPerPage={mailsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                    itemId='id'
                    columns={[
                        {
                            label: 'Gesendet am',
                            itemTransform: (mail) =>
                                formatStarjackDate(mail.sentAt),
                        },
                        {
                            label: 'Betreffe',
                            itemKey: 'subject',
                        },
                        {
                            label: 'Mail öffnen',
                            itemTransform: (mail) => (
                                <IconButton
                                    aria-label='Mail öffnen'
                                    onClick={() => {
                                        setMailDetailConfig({
                                            isOpen: true,
                                            mail,
                                        });
                                    }}
                                >
                                    <SvgIcon fontSize='medium'>
                                        <EnvelopeOpenIcon />
                                    </SvgIcon>
                                </IconButton>
                            ),
                            options: {
                                headerCellProps: {
                                    align: 'center',
                                },
                                rowCellProps: {
                                    align: 'center',
                                },
                            },
                        },
                    ]}
                />
            </Box>

            <Drawer
                anchor='right'
                open={!!mailDetailConfig.isOpen}
                onClose={onCloseDrawer}
            >
                <MailDrawerContent
                    mail={mailDetailConfig.mail}
                    onClose={onCloseDrawer}
                />
            </Drawer>
        </>
    );
};
